import React, { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { finApi, money } from "@/lib/financeApi";
import { Empty, ErrorBox, Loading, useLoad } from "@/components/nds/windows/finance/parts";
import { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import { LandlordSummary, OrgLedgerResidents } from "@/components/nds/windows/finance/OrgLedgerParts";

const COLS = [["invoiced", "Invoice Sent"], ["received", "Payment Received from Employer"], ["distributed", "Distributed to Residents"], ["net", "TEA Net Position"]];
const TOTALS = [["invoiced", "Total invoiced to employers"], ["received", "Total received from employers"], ["distributed", "Total distributed to residents"], ["held", "Total currently held by TEA"]];
const tone = (v) => (v < 0 ? "text-red-300" : "text-nex-silver");
const monthLabel = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

function Totals({ t }) {
  return (
    <div data-testid="org-ledger-totals" className="grid grid-cols-2 lg:grid-cols-4 gap-3">
      {TOTALS.map(([k, l]) => (
        <div key={k} data-testid={`org-ledger-total-${k}`} className={`nds-card p-4 ${k === "held" ? "ring-1 ring-nex-gold/40" : ""}`}>
          <div className="nds-label">{l}</div>
          <div className={`mt-2 font-mono text-2xl ${k === "held" ? "text-nex-goldbright" : tone(t[k])}`}>{money(t[k])}</div>
        </div>))}
    </div>
  );
}

function WeekRow({ w, onSaved }) {
  const [open, setOpen] = useState(false);
  const waiting = w.residents.filter((x) => x.status === "waiting").length;
  const selfPay = w.residents.filter((x) => x.status === "self_pay").length;
  return (
    <>
      <tr data-testid={`org-ledger-week-${w.week}`} onClick={() => setOpen(!open)} className="border-t border-white/5 cursor-pointer hover:bg-nex-gold/5 transition-colors">
        <td className="px-3 py-2 text-nex-silver whitespace-nowrap">{open ? <ChevronDown className="inline h-3.5 w-3.5 mr-1" /> : <ChevronRight className="inline h-3.5 w-3.5 mr-1" />}Week ending {w.week}
          <div className="text-[10px] text-nex-muted pl-5">{w.invoices.length} invoice{w.invoices.length === 1 ? "" : "s"}{selfPay ? ` · ${selfPay} self-pay` : ""} · {w.residents.length} row{w.residents.length === 1 ? "" : "s"}{waiting ? ` · ${waiting} waiting` : ""}</div></td>
        {COLS.map(([k]) => <td key={k} data-testid={`org-ledger-week-${w.week}-${k}`} className={`px-3 py-2 text-right font-mono ${k === "net" ? "text-nex-goldbright" : tone(w[k])}`}>{money(w[k])}</td>)}
      </tr>
      {open && <tr data-testid={`org-ledger-week-${w.week}-residents`}><td colSpan={5} className="bg-black/30 border-t border-white/5"><OrgLedgerResidents rows={w.residents} onSaved={onSaved} /></td></tr>}
    </>
  );
}

function ViewTabs({ allTime, setMonth }) {
  return (
    <div data-testid="org-ledger-view" className="flex border border-white/10">
      {[["month", "Month"], ["all", "All-Time"]].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setMonth(k === "all" ? "all" : "")} data-testid={`org-ledger-view-${k}`}
          className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors ${(k === "all") === allTime ? "bg-nex-gold/15 text-nex-gold" : "text-nex-muted hover:text-nex-silver"}`}>{l}</button>))}
    </div>
  );
}

export default function FinOrgLedgerContent() {
  const isSuper = useIsSuper();
  const [month, setMonth] = useState("");
  const allTime = month === "all";
  const [s, load] = useLoad(() => (isSuper ? finApi.get("/org-ledger", month ? { month } : {}) : Promise.resolve(null)), [isSuper, month]);
  if (!isSuper) return <Empty testid="org-ledger-forbidden">Only a Super Admin can view the TEA Organization Ledger.</Empty>;
  if (s.loading && !s.data) return <Loading testid="org-ledger-loading" label="Loading TEA's ledger…" />;
  if (s.error) return <ErrorBox testid="org-ledger-error" error={s.error} onRetry={load} />;
  if (!s.data) return null;
  const d = s.data;
  const label = allTime ? "all time" : monthLabel(d.month);
  return (
    <div data-testid="org-ledger" className="space-y-5 p-4 sm:p-5">
      <div className="flex flex-wrap items-center gap-3">
        <ViewTabs allTime={allTime} setMonth={setMonth} />
        <p className="text-xs text-nex-muted flex-1 min-w-[240px]">TEA's own money, separate from resident balances. Each amount counts in the month it happened. Distributed is pay minus rent and case management. Archived residents are left out. Pencil edits change the resident's real paycheck split.</p>
        {!allTime && <select value={month || d.month} onChange={(e) => setMonth(e.target.value)} data-testid="org-ledger-month" className="[color-scheme:dark] bg-black/40 border border-white/10 text-nex-silver text-xs px-2 py-1.5 rounded-sm">
          {(d.months.length ? d.months : [d.month]).slice().reverse().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </select>}
      </div>
      <Totals t={d.totals} />
      {d.weeks.length ? (
        <div className="overflow-x-auto border border-white/10">
          <table data-testid="org-ledger-table" className="w-full min-w-[720px] text-left text-sm">
            <thead><tr className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted"><th className="px-3 py-2 font-normal">Pay week</th>
              {COLS.map(([k, l]) => <th key={k} className="px-3 py-2 font-normal text-right">{l}</th>)}</tr></thead>
            <tbody>{d.weeks.map((w) => <WeekRow key={w.week} w={w} onSaved={load} />)}</tbody>
          </table>
        </div>) : <Empty testid="org-ledger-empty">No employer invoices, payments or distributions in {label}.</Empty>}
      <LandlordSummary l={d.landlord} label={label} />
    </div>
  );
}
